import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Building2, Briefcase } from 'lucide-react';
import { INDUSTRIES } from '@/constants/industries';

const empty = {
    type: 'client', name: '', industry: '', location: '', description: '',
    image_url: '', logo_url: '', website: '', contact_email: '', contact_phone: '',
    certifications: '', featured: false, active: true,
    affiliated_year: '', employees: '', surface: '',
};

export default function AdminCatalog() {
    const [entries, setEntries] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = () => api.get('/admin/catalog').then((r) => setEntries(r.data));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
    const openEdit = (e) => {
        setEditing(e.id);
        setForm({ ...e, certifications: (e.certifications || []).join(', ') });
        setShowForm(true);
    };

    const save = async (ev) => {
        ev.preventDefault();
        const payload = {
            ...form,
            certifications: form.certifications ? form.certifications.split(',').map((s) => s.trim()).filter(Boolean) : [],
        };
        try {
            if (editing) await api.patch(`/admin/catalog/${editing}`, payload);
            else await api.post('/admin/catalog', payload);
            toast.success(editing ? 'Entrada actualizada' : 'Entrada creada');
            setShowForm(false); load();
        } catch (err) { toast.error(apiErrorMessage(err, 'Error')); }
    };

    const remove = async (id) => {
        if (!window.confirm('¿Eliminar esta entrada del catálogo?')) return;
        await api.delete(`/admin/catalog/${id}`);
        toast.success('Eliminada'); load();
    };

    const set = (k, v) => setForm({ ...form, [k]: v });

    return (
        <AdminLayout title="Catálogo público">
            <PageHeader title="Catálogo público" subtitle="◆ Empresas con distintivo visibles en la web" accent="#111111">
                <button onClick={openCreate} data-testid="catalog-create-btn"
                    className="bg-[#E08B3C] text-white px-5 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90">
                    <Plus className="w-4 h-4" /> Nueva entrada
                </button>
            </PageHeader>

            {showForm && (
                <form onSubmit={save} className="border border-gray-200 p-6 mb-6 bg-white grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 grid grid-cols-2 gap-2 bg-gray-50 p-1 border border-gray-200">
                        <button type="button" onClick={() => set('type', 'client')}
                            className={`py-2 font-bold text-sm inline-flex items-center justify-center gap-2 ${form.type === 'client' ? 'bg-[#1F8FB8] text-white' : 'text-gray-600'}`}>
                            <Building2 className="w-4 h-4" /> Cliente principal
                        </button>
                        <button type="button" onClick={() => set('type', 'provider')}
                            className={`py-2 font-bold text-sm inline-flex items-center justify-center gap-2 ${form.type === 'provider' ? 'bg-[#E08B3C] text-white' : 'text-gray-600'}`}>
                            <Briefcase className="w-4 h-4" /> Proveedor
                        </button>
                    </div>
                    <Field label="Nombre *" v={form.name} on={(v) => set('name', v)} req />
                    <div>
                        <label className="text-sm font-medium block mb-1">Giro / Industria *</label>
                        <select value={form.industry} onChange={(e) => set('industry', e.target.value)} required
                            data-testid="catalog-industry-select"
                            className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none bg-white">
                            <option value="">— Selecciona un rubro —</option>
                            {INDUSTRIES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </div>
                    <Field label="Ubicación *" v={form.location} on={(v) => set('location', v)} req placeholder="Ciudad, Estado" />
                    <Field label="Año afiliación" v={form.affiliated_year || ''} on={(v) => set('affiliated_year', v)} placeholder="2025" />
                    <Field label="Imagen URL (hero)" v={form.image_url} on={(v) => set('image_url', v)} placeholder="https://..." />
                    <Field label="Logo URL" v={form.logo_url} on={(v) => set('logo_url', v)} placeholder="https://..." />
                    <Field label="Website" v={form.website} on={(v) => set('website', v)} />
                    <Field label="Teléfono contacto" v={form.contact_phone} on={(v) => set('contact_phone', v)} />
                    <Field label="Email contacto" v={form.contact_email} on={(v) => set('contact_email', v)} />
                    <Field label="Personal" v={form.employees || ''} on={(v) => set('employees', v)} placeholder="500+" />
                    <Field label="Superficie" v={form.surface || ''} on={(v) => set('surface', v)} placeholder="15,000 m²" />
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium block mb-1">Distintivos (separados por coma)</label>
                        <input value={form.certifications} onChange={(e) => set('certifications', e.target.value)}
                            placeholder="NOM-001-STPS-2008, ISO 9001:2015"
                            className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium block mb-1">Descripción</label>
                        <textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)}
                            className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none resize-none" />
                    </div>
                    <div className="md:col-span-2 flex items-center gap-4">
                        <label className="text-sm inline-flex items-center gap-2">
                            <input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} /> Destacado
                        </label>
                        <label className="text-sm inline-flex items-center gap-2">
                            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Activo
                        </label>
                    </div>
                    <div className="md:col-span-2 flex gap-2">
                        <button type="submit" className="bg-[#0A0A0A] text-white px-5 py-2 font-bold hover:bg-black">{editing ? 'Actualizar' : 'Crear'}</button>
                        <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-5 py-2 hover:bg-gray-50">Cancelar</button>
                    </div>
                </form>
            )}

            <div className="border border-gray-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="text-left px-4 py-3">Nombre</th>
                            <th className="text-left px-4 py-3">Tipo</th>
                            <th className="text-left px-4 py-3">Giro</th>
                            <th className="text-left px-4 py-3">Ubicación</th>
                            <th className="text-left px-4 py-3">Activo</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {entries.map((e) => (
                            <tr key={e.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium">{e.name}</td>
                                <td className="px-4 py-3"><span className="text-xs px-2 py-1 border border-gray-300">{e.type === 'client' ? 'Cliente' : 'Proveedor'}</span></td>
                                <td className="px-4 py-3 text-gray-600">{e.industry}</td>
                                <td className="px-4 py-3 text-gray-600">{e.location}</td>
                                <td className="px-4 py-3"><span className={e.active ? 'text-[#1E9E47]' : 'text-gray-400'}>{e.active ? '● Sí' : '○ No'}</span></td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => openEdit(e)} className="text-xs underline mr-3"><Edit2 className="w-4 h-4 inline" /></button>
                                    <button onClick={() => remove(e.id)} className="text-xs text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                                </td>
                            </tr>
                        ))}
                        {entries.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Sin entradas. Crea la primera.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </AdminLayout>
    );
}

function Field({ label, v, on, req, placeholder }) {
    return (
        <div>
            <label className="text-sm font-medium block mb-1">{label}</label>
            <input value={v} onChange={(e) => on(e.target.value)} required={req} placeholder={placeholder}
                className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none" />
        </div>
    );
}
